import { normalizeCasing } from '../../helpers';

// OpenSearch's default index.max_result_window — the most a single search can
// return. Matches SearchIndexExporter#export_from_opensearch and the live
// path's resultsPerPage, so every route into the chart shares one ceiling.
const MAX_ROWS = 10000;

// One-shot full-collection fetch for the in-memory path when no injected
// `data-chart-for` payload exists (admin preview / cold cache). Mirrors
// SearchIndexExporter#export_from_opensearch exactly — a match_all up to 10k
// docs, dropping the internal id/external_id — so preview renders from the same
// rows the published page would ship. Casing is normalized like the injected
// path (readChartRows). Returns a flat row array; throws on a non-OK response.
export async function fetchAllRows(host, index, { previewToken } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  // Prefer the utils-pack helper, but fall back to the server-rendered meta tag
  // (which doesn't depend on that pack having loaded). Only set the header when
  // we actually have a token — a plain-object header value of `undefined` is
  // coerced to the string "undefined", which Rails rejects with a 422 and the
  // fetch then silently falls back to live search.
  const csrf = window.getCsrfToken?.() || document.querySelector('meta[name="csrf-token"]')?.content || '';
  if (csrf) headers['X-CSRF-Token'] = csrf;
  if (previewToken) headers['x-preview-token'] = previewToken;

  const response = await fetch(`${host}/${encodeURIComponent(index)}/_search`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query: { match_all: {} }, size: MAX_ROWS }),
  });
  if (!response.ok) throw new Error(`OpenSearch ${response.status} for ${index}`);

  const data = await response.json();
  // Diagnostic only — visible in the browser console, not to page visitors.
  // A collection past the window renders an incomplete chart that still looks
  // complete, so leave a trace someone can find when a chart is questioned.
  // The published (cached) payload truncates at the same ceiling, so this is
  // not specific to the preview path.
  const total = data.hits?.total?.value;
  if (typeof total === 'number' && total > MAX_ROWS) {
    console.warn(
      `nv-chart: "${index}" has ${total} documents but only the first ${MAX_ROWS} are charted ` +
      '(OpenSearch index.max_result_window). The chart is incomplete.'
    );
  }
  return (data.hits?.hits || []).map(hit => {
    const { id, external_id, ...rest } = hit._source || {};
    return normalizeCasing(rest);
  });
}
